(() => {
  const ROOT_SELECTOR =
    '[data-looping-words]';

  const LIST_SELECTOR =
    '[data-looping-words-list]';

  const TEXT_SELECTOR =
    '[data-looping-words-text]';

  const SELECTOR_SELECTOR =
    '[data-looping-words-selector]';

  const TOGGLE_SELECTOR =
    '[data-looping-words-toggle]';

  const DESKTOP_TABLET_QUERY =
    '(min-width: 750px)';

  const DEFAULT_INTERVAL = 3;

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  const displayQuery =
    window.matchMedia(
      DESKTOP_TABLET_QUERY
    );

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function hasDependencies() {
    return Boolean(window.gsap);
  }

  function registerDependencies() {
    if (!hasDependencies()) {
      return false;
    }

    return true;
  }


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getRoots(
    scope = document
  ) {
    const roots = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    return roots;
  }


  /* =========================================
     CREATE INSTANCE
     ========================================= */

  function createLoopingWords(root) {
    if (!registerDependencies()) {
      return null;
    }

    if (
      root._wamboltLoopingWords &&
      typeof root
        ._wamboltLoopingWords
        .destroy === 'function'
    ) {
      root
        ._wamboltLoopingWords
        .destroy();
    }

    const wordList =
      root.querySelector(
        LIST_SELECTOR
      );

    const selectorElement =
      root.querySelector(
        SELECTOR_SELECTOR
      );

    const toggleButton =
      root.querySelector(
        TOGGLE_SELECTOR
      );

    if (
      !wordList ||
      !selectorElement
    ) {
      return null;
    }

    const words =
      Array.from(
        wordList.children
      );

    if (!words.length) {
      return null;
    }

    const originalOrder =
      [...words];

    const totalWords =
      words.length;

    const wordHeight =
      100 /
      totalWords;

    const parsedInterval =
      Number.parseFloat(
        root.getAttribute(
          'data-looping-words-interval'
        )
      );

    const interval =
      Number.isFinite(parsedInterval)
        ? Math.max(
            parsedInterval,
            0
          )
        : DEFAULT_INTERVAL;

    const controller =
      new AbortController();

    const { signal } = controller;

    const reducedMotion =
      reducedMotionQuery.matches;

    let currentIndex = 0;
    let userPaused = false;
    let isInView = true;
    let documentHidden =
      document.hidden;

    let timeline = null;
    let wordTween = null;
    let selectorTween = null;
    let resizeObserver = null;
    let intersectionObserver = null;
    let resizeFrame = null;
    let destroyed = false;


    /* =========================================
       ACTIVE PHRASE
       ========================================= */

    function getCenterWord() {
      const centerIndex =
        (
          currentIndex +
          1
        ) %
        totalWords;

      return words[
        centerIndex
      ];
    }

    function getCenterText() {
      const centerWord =
        getCenterWord();

      if (!centerWord) {
        return null;
      }

      return (
        centerWord.querySelector(
          TEXT_SELECTOR
        ) ||
        centerWord
      );
    }

    function updateActiveState() {
      const centerWord =
        getCenterWord();

      words.forEach((word) => {
        word.classList.remove(
          'is-active'
        );
      });

      if (centerWord) {
        centerWord.classList.add(
          'is-active'
        );
      }
    }


    /* =========================================
       SELECTOR GEOMETRY
       ========================================= */

    function getSelectorGeometry() {
      const centerText =
        getCenterText();

      if (!centerText) {
        return null;
      }

      const rootRect =
        root.getBoundingClientRect();

      const textRect =
        centerText
          .getBoundingClientRect();

      const rootStyles =
        window.getComputedStyle(
          root
        );

      const textStyles =
        window.getComputedStyle(
          centerText
        );

      const fontSize =
        Number.parseFloat(
          textStyles.fontSize
        ) || 16;

      const gapXRatio =
        Number.parseFloat(
          rootStyles.getPropertyValue(
            '--loop-selector-gap-x'
          )
        ) || 0.22;

      const gapYRatio =
        Number.parseFloat(
          rootStyles.getPropertyValue(
            '--loop-selector-gap-y'
          )
        ) || 0.08;

      const gapX =
        fontSize *
        gapXRatio;

      const gapY =
        fontSize *
        gapYRatio;

      const centerY =
        (
          textRect.top -
          rootRect.top
        ) +
        (
          textRect.height /
          2
        );

      const desiredWidth =
        textRect.width +
        gapX * 2;

      return {
        width: Math.min(
          desiredWidth,
          rootRect.width
        ),

        height:
          textRect.height +
          gapY * 2,

        top: centerY,
      };
    }

    function updateSelector(
      animate = true
    ) {
      const geometry =
        getSelectorGeometry();

      if (!geometry) {
        return;
      }

      updateActiveState();

      const properties = {
        width:
          `${geometry.width}px`,

        height:
          `${geometry.height}px`,

        top:
          `${geometry.top}px`,
      };

      if (selectorTween) {
        selectorTween.kill();
        selectorTween = null;
      }

      if (
        animate &&
        !reducedMotion
      ) {
        selectorTween =
          gsap.to(
            selectorElement,
            {
              ...properties,

              duration: 0.5,

              ease: 'expo.out',

              overwrite: true,

              onComplete() {
                selectorTween = null;
              },
            }
          );

        return;
      }

      gsap.set(
        selectorElement,
        properties
      );
    }


    /* =========================================
       LOOP MOVEMENT
       ========================================= */

    function moveWords() {
      currentIndex += 1;

      updateActiveState();

      if (wordTween) {
        wordTween.kill();
      }

      wordTween =
        gsap.to(
          wordList,
          {
            yPercent:
              -wordHeight *
              currentIndex,

            duration: 1.2,

            ease:
              'elastic.out(1, 0.85)',

            overwrite: true,

            onUpdate() {
              const geometry =
                getSelectorGeometry();

              if (!geometry) {
                return;
              }

              gsap.set(
                selectorElement,
                {
                  top:
                    `${geometry.top}px`,
                }
              );
            },

            onComplete() {
              wordTween = null;

              if (
                currentIndex >=
                totalWords - 3
              ) {
                wordList.appendChild(
                  wordList.children[0]
                );

                currentIndex -= 1;

                gsap.set(
                  wordList,
                  {
                    yPercent:
                      -wordHeight *
                      currentIndex,
                  }
                );

                words.push(
                  words.shift()
                );
              }

              updateActiveState();
              updateSelector(false);
              syncPlayback();
            },
          }
        );

      const geometry =
        getSelectorGeometry();

      if (geometry) {
        if (selectorTween) {
          selectorTween.kill();
        }

        selectorTween =
          gsap.to(
            selectorElement,
            {
              width:
                `${geometry.width}px`,

              height:
                `${geometry.height}px`,

              duration: 0.5,

              ease: 'expo.out',

              overwrite: true,

              onComplete() {
                selectorTween = null;
              },
            }
          );
      }
    }


    /* =========================================
       PLAYBACK STATE
       ========================================= */

    function shouldPause() {
      return (
        userPaused ||
        !isInView ||
        documentHidden
      );
    }

    function updateToggleButton() {
      if (!toggleButton) {
        return;
      }

      toggleButton.setAttribute(
        'aria-pressed',
        userPaused
          ? 'true'
          : 'false'
      );

      toggleButton.setAttribute(
        'aria-label',
        userPaused
          ? 'Resume packing phrase animation'
          : 'Pause packing phrase animation'
      );
    }

    function syncPlayback() {
      if (
        reducedMotion ||
        totalWords < 2
      ) {
        return;
      }

      const pause =
        shouldPause();

      [
        timeline,
        wordTween,
        selectorTween,
      ]
        .filter(Boolean)
        .forEach((animation) => {
          if (pause) {
            animation.pause();
          } else {
            animation.resume();
          }
        });
    }

    function togglePlayback() {
      userPaused =
        !userPaused;

      updateToggleButton();
      syncPlayback();
    }


    /* =========================================
       INITIAL STATE
       ========================================= */

    updateActiveState();
    updateSelector(false);
    updateToggleButton();

    if (
      !reducedMotion &&
      totalWords > 1
    ) {
      timeline =
        gsap.timeline({
          repeat: -1,
          delay: 1,
        });

      timeline
        .call(moveWords)
        .to(
          {},
          {
            duration: interval,
          }
        );
    }


    /* =========================================
       USER CONTROL
       ========================================= */

    toggleButton?.addEventListener(
      'click',
      togglePlayback,
      {
        signal,
      }
    );


    /* =========================================
       VISIBILITY
       ========================================= */

    document.addEventListener(
      'visibilitychange',
      () => {
        documentHidden =
          document.hidden;

        syncPlayback();
      },
      {
        signal,
      }
    );

    if (
      'IntersectionObserver' in window
    ) {
      intersectionObserver =
        new IntersectionObserver(
          (entries) => {
            const entry =
              entries[0];

            if (!entry) {
              return;
            }

            isInView =
              entry.isIntersecting;

            syncPlayback();
          },
          {
            threshold: 0.01,
          }
        );

      intersectionObserver.observe(
        root
      );
    }


    /* =========================================
       RESIZE
       ========================================= */

    function scheduleResize() {
      if (
        destroyed ||
        resizeFrame !== null
      ) {
        return;
      }

      resizeFrame =
        window.requestAnimationFrame(
          () => {
            resizeFrame = null;

            if (
              destroyed ||
              !root.isConnected
            ) {
              return;
            }

            updateSelector(false);
          }
        );
    }

    window.addEventListener(
      'resize',
      scheduleResize,
      {
        passive: true,
        signal,
      }
    );

    if (
      'ResizeObserver' in window
    ) {
      resizeObserver =
        new ResizeObserver(
          scheduleResize
        );

      resizeObserver.observe(root);
    }

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(
        () => {
          if (
            !destroyed &&
            root.isConnected
          ) {
            scheduleResize();
          }
        }
      );
    }


    /* =========================================
       INSTANCE
       ========================================= */

    const instance = {
      destroy() {
        if (destroyed) {
          return;
        }

        destroyed = true;

        controller.abort();

        if (resizeFrame !== null) {
          window.cancelAnimationFrame(
            resizeFrame
          );

          resizeFrame = null;
        }

        if (timeline) {
          timeline.kill();
          timeline = null;
        }

        if (wordTween) {
          wordTween.kill();
          wordTween = null;
        }

        if (selectorTween) {
          selectorTween.kill();
          selectorTween = null;
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        if (intersectionObserver) {
          intersectionObserver.disconnect();
          intersectionObserver = null;
        }

        gsap.killTweensOf(
          wordList
        );

        gsap.killTweensOf(
          selectorElement
        );

        originalOrder.forEach(
          (word) => {
            wordList.appendChild(word);
          }
        );

        gsap.set(
          wordList,
          {
            clearProps: 'transform',
          }
        );

        gsap.set(
          selectorElement,
          {
            clearProps:
              'width,height,top',
          }
        );

        words.forEach((word) => {
          word.classList.remove(
            'is-active'
          );
        });

        delete root
          ._wamboltLoopingWords;
      },
    };

    root._wamboltLoopingWords =
      instance;

    return instance;
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initLoopingWords(
    scope = document
  ) {
    const roots =
      getRoots(scope);

    if (!roots.length) {
      return true;
    }

    if (!displayQuery.matches) {
      roots.forEach((root) => {
        root
          ._wamboltLoopingWords
          ?.destroy();
      });

      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    roots.forEach(
      createLoopingWords
    );

    return true;
  }

  function destroyLoopingWords(
    scope = document
  ) {
    getRoots(scope).forEach(
      (root) => {
        root
          ._wamboltLoopingWords
          ?.destroy();
      }
    );
  }

  function rebuildLoopingWords() {
    destroyLoopingWords(
      document
    );

    if (
      !initLoopingWords(
        document
      )
    ) {
      scheduleDependencyRetry();
    }
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      window.clearTimeout(
        dependencyRetryTimer
      );

      dependencyRetryTimer = null;
    }
  }

  function scheduleDependencyRetry() {
    if (
      dependencyRetryTimer !== null ||
      !displayQuery.matches
    ) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Packing Loop could not initialize because GSAP is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer = null;

          if (
            initLoopingWords(
              document
            )
          ) {
            dependencyRetryAttempts = 0;
            clearDependencyRetry();
            return;
          }

          scheduleDependencyRetry();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  function start() {
    if (
      initLoopingWords(
        document
      )
    ) {
      dependencyRetryAttempts = 0;
      clearDependencyRetry();
      return;
    }

    scheduleDependencyRetry();
  }


  /* =========================================
     MEDIA QUERY CHANGES
     ========================================= */

  function addMediaQueryListener(
    query,
    handler
  ) {
    if (
      typeof query.addEventListener ===
      'function'
    ) {
      query.addEventListener(
        'change',
        handler
      );

      return;
    }

    if (
      typeof query.addListener ===
      'function'
    ) {
      query.addListener(handler);
    }
  }

  addMediaQueryListener(
    displayQuery,
    rebuildLoopingWords
  );

  addMediaQueryListener(
    reducedMotionQuery,
    rebuildLoopingWords
  );


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        !initLoopingWords(
          event.target
        )
      ) {
        scheduleDependencyRetry();
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyLoopingWords(
        event.target
      );
    }
  );
})();